import { ApiNames } from 'components/Common/ApiManagement/apiServices';
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { USERTYPES, USER_STATUS } from 'components/Common/Constant';
import { useState, useEffect, useRef } from 'react';
import './EditModal.css';

const EditModal = ({ modal, setModal, handleSubmit, setEditUserData, editUserData, isModifying }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [orgInputValue, setOrgInputValue] = useState("");
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (modal) {
      // Scroll to top when modal opens
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Focus first input
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [modal]);

  const handleRadioClick = (value) => {
    setEditUserData((prev) => ({
      ...prev,
      userType: value
    }));
  }

  const handleInputChange = (e) => {
    setEditUserData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  }

  const fetchSuggestions = async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await axiosInstance.get(`${ApiNames.LIST_ORGANIZATION}?search=${query}`)
      setSuggestions(data.data);
      setDropdownOpen(data.data.length > 0)
    } catch (error) {
      console.error(error)
    }
  };

  const handleOrgInputChange = e => {
    const value = e.target.value;
    setOrgInputValue(value);
    setEditUserData((prev) => ({
      ...prev,
      branch: e.target.value
    }));
    fetchSuggestions(value);
  };

  const handleSelect = org => {
    setOrgInputValue(org.branch);
    setSuggestions([]);
    setDropdownOpen(false);
    setEditUserData((prev) => ({
      ...prev,
      org_id: org._id
    }));
  };

  const toggle = () => setModal(!modal)

  if (!modal) return null;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && toggle()}
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 99999,
        overflowY: 'auto',
        paddingTop: '3rem',
        paddingLeft: '1rem',
        paddingRight: '1rem'
      }}
    >
      <div className="edit-modal-container">
        <div className="edit-modal-content">
          {/* Header */}
          <div className="edit-modal-header">
            <h5 className="edit-modal-title">Edit User Details</h5>
            <button type="button" className="edit-modal-close" onClick={toggle}>×</button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="edit-modal-body">
              <div className="edit-form-row">
                <div className="edit-form-col">
                  <label className="edit-form-label">First Name</label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    name="firstname"
                    className="edit-form-input"
                    placeholder="Enter first name"
                    value={editUserData?.firstname || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="edit-form-col">
                  <label className="edit-form-label">Last Name</label>
                  <input
                    type="text"
                    name="lastname"
                    className="edit-form-input"
                    placeholder="Enter last name"
                    value={editUserData?.lastname || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="edit-form-group">
                <label className="edit-form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="edit-form-input"
                  placeholder="Enter email address"
                  value={editUserData?.email || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="edit-form-group edit-org-group">
                <label className="edit-form-label">Organization</label>
                <input
                  name="organization"
                  type="text"
                  className="edit-form-input"
                  placeholder="Choose or create organization"
                  autoComplete='off'
                  onChange={handleOrgInputChange}
                  value={orgInputValue || editUserData.branch || ""}
                  onFocus={() => orgInputValue && fetchSuggestions(orgInputValue)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                />
                {dropdownOpen && suggestions.length > 0 && (
                  <div className="edit-org-dropdown">
                    {suggestions.map((org, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelect(org)}
                        className="edit-org-option"
                      >
                        {org.branch}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="edit-form-group">
                <label className="edit-form-label">User Type</label>
                <div className="edit-radio-group">
                  <label className="edit-radio-item">
                    <input
                      type="radio"
                      name="userType"
                      value={USERTYPES.Standard}
                      checked={editUserData?.userType === USERTYPES.Standard}
                      onClick={() => handleRadioClick(USERTYPES.Standard)}
                    />
                    <span>Standard</span>
                  </label>
                  <label className="edit-radio-item">
                    <input
                      type="radio"
                      name="userType"
                      value={USERTYPES.Organization}
                      checked={editUserData?.userType === USERTYPES.Organization}
                      onClick={() => handleRadioClick(USERTYPES.Organization)}
                    />
                    <span>Organization Admin</span>
                  </label>
                  <label className="edit-radio-item">
                    <input
                      type="radio"
                      name="userType"
                      value={USERTYPES.Global}
                      checked={editUserData?.userType === USERTYPES.Global}
                      onClick={() => handleRadioClick(USERTYPES.Global)}
                    />
                    <span>Global Admin</span>
                  </label>
                </div>
              </div>


            </div>

            {/* Footer */}
            <div className="edit-modal-footer">
              <button
                type="button"
                className="edit-btn edit-btn-cancel"
                onClick={toggle}
                disabled={isModifying}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit-btn edit-btn-submit"
                style={{ backgroundColor: '#3b82f6', color: 'white' }}
                disabled={isModifying}
              >
                {isModifying ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditModal
